import PropTypes from 'prop-types';
import React from 'react';
import { IconButton } from 'react-toolbox';
import ColoredButton from 'components/ColoredButton';
import ConfirmDialog from 'components/ConfirmDialog';
import ReactTooltip from 'react-tooltip';
import EditableTableCell from './editableTableCell';
import S from '../styles.scss';
import { isEqual } from 'lodash';

class EditableTableRow extends React.Component {
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.state = { isDirty: false, renderDeleteDialog: false };
  }

  getChildContext = () => ({
    onChange: this.handleChange,
    disabled: this.props.disabled,
    readOnly: this.props.readOnly,
  });

  componentDidMount = () => {
    const { onSave, onDelete } = this.props;
    if (!this.context.registerHeaders) return;
    const children = React.Children.toArray(this.props.children).map(child => (
      child && { props: { display: child.props.display, label: child.props.label } }));
    this.context.registerHeaders(children, !!onDelete, !!onSave);
  }

  componentWillReceiveProps = (nextProps, nextContext) => {
    const { onSave, onDelete } = this.props;
    if (!this.context.registerHeaders) return;
    const currentChildren = React.Children.toArray(this.props.children).map(child => (
      child && { props: { display: child.props.display, label: child.props.label } }));
    const nextChildren = React.Children.toArray(nextProps.children).map(child => (
      child && { props: { display: child.props.display, label: child.props.label } }));
    if (!isEqual(currentChildren, nextChildren)) {
      this.context.registerHeaders(nextChildren, !!onDelete, !!onSave);
    }
    if (!nextContext.isEditable && this.state.isDirty) this.setState({ isDirty: false });
  }

  handleChange = (event, cb) => {
    cb(event);
    this.setState({ isDirty: true });
  }

  handleSave = () => {
    this.setState({ isDirty: false });
    if (!this.props.onSave) return;
    this.props.onSave();
  }

  handleDelete = () => {
    const { onDelete, deleteDialog } = this.props;
    const { renderDeleteDialog } = this.state;
    if (!onDelete) return;
    if (deleteDialog && !renderDeleteDialog) this.showDeleteDialog();
    else this.props.onDelete();
  }

  hideDeleteDialog = () => (this.setState({ renderDeleteDialog: false }))
  showDeleteDialog = () => (this.setState({ renderDeleteDialog: true }))

  renderSaveButton = () => {
    const { isDirty } = this.state;
    const { disabled, totalMismatch, role, message, invalidNumber, id } = this.props;
    const totalDisabled = totalMismatch && role !== 'admin';
    const val = message && totalDisabled
      ? <span className={S.message}>{message}</span> : (<div
        data-tip
        data-tip-disable={!invalidNumber}
        data-for={`${id}InvalidNumberError`}
      ><ColoredButton
        disabled={disabled || totalDisabled || invalidNumber || !isDirty}
        label={totalMismatch ? 'FORCE SAVE' : 'SAVE'}
        onClick={this.handleSave}
        color={totalMismatch ? 'red' : 'green'}
      /><ReactTooltip
        id={`${id}InvalidNumberError`}
        place="top"
        effect="solid"
      >
        Invalid PO number. Please ensure the PO number is 9 digits or 8 digits prefixed by
        &#39;PR&#39; and is not the project number.
      </ReactTooltip></div>);
    return (<EditableTableCell
      key="save" value={val} className={S.saveCell}
    >{val}</EditableTableCell>);
  }

  renderDeleteButton = () => {
    const { disabled, hideDelete } = this.props;
    const isDisabled = disabled || hideDelete;
    const val = <IconButton disabled={isDisabled} icon="delete" onClick={this.handleDelete} />;
    return (<EditableTableCell
      key="deletectrl" value={val} className={S.deleteCell}
    >{!this.props.isNew && val}</EditableTableCell>);
  }

  renderBlankCell = () => (<td className={S.cell} />)

  renderCells = () => {
    const { onDelete, onSave, readOnly } = this.props;
    const children = React.Children.toArray(this.props.children);
    if (!this.context.isEditable || this.context.hasCustomControls) return children;
    const cells = [...children];
    if (onDelete && !readOnly) cells.unshift(this.renderDeleteButton());
    if (onDelete && readOnly) cells.unshift(this.renderBlankCell());
    if (onSave && !readOnly) cells.push(this.renderSaveButton());
    if (onSave && readOnly) cells.push(this.renderBlankCell());
    return cells;
  }

  render = () => {
    const { toggleLineItem, expandableRow, className = '' } = this.props;
    if (!this.context.isEditable && this.props.isNew) return null;
    const { deleteDialog, deleteError } = this.props;
    const { renderDeleteDialog } = this.state;
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    return (<tr
      id={this.props.isNew ? 'installerPayTutorial3' : ''}
      className={`${expandableRow ? S.expandableContentRow : S.contentRow} ${className}`}
      style={this.props.style}
      onClick={toggleLineItem}
    >
      {this.renderCells()}
      {deleteDialog && <ConfirmDialog
        show={renderDeleteDialog}
        confirmation="Are you sure you want to delete this record?"
        dismiss={this.hideDeleteDialog}
        cancel={this.hideDeleteDialog}
        proceed={this.handleDelete}
        error={deleteError}
      />}
    </tr>);
  }
}

EditableTableRow.contextTypes = {
  isEditable: PropTypes.bool,
  hasCustomControls: PropTypes.bool,
  registerHeaders: PropTypes.func,
};

EditableTableRow.childContextTypes = {
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
};

EditableTableRow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  invalidNumber: PropTypes.bool,
  isNew: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  toggleLineItem: PropTypes.func,
  disabled: PropTypes.bool,
  totalMismatch: PropTypes.bool,
  role: PropTypes.string,
  message: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  deleteDialog: PropTypes.bool,
  deleteError: PropTypes.string,
  readOnly: PropTypes.bool,
  hideDelete: PropTypes.bool,
  expandableRow: PropTypes.bool,
};

EditableTableRow.defaultProps = {
  id: 'new',
  isNew: false,
  children: null,
  disabled: false,
  invalidNumber: false,
  totalMismatch: false,
  message: '',
  className: '',
  style: {},
  deleteDialog: false,
  readOnly: false,
  hideDelete: false,
  expandableRow: false,
};

export default EditableTableRow;



// WEBPACK FOOTER //
// ./client/app/shared/components/EditableTable/components/editableTableRow.jsx